import {
  Sparkles,
  List,
  Users,
  Search,
  Calendar,
  MousePointerClick,
  TrendingUp,
  MessagesSquare,
  Handshake,
  CheckSquare,
  FileText,
  MessageCircle,
  Video,
  Building2,
} from "lucide-react";
import { useMemo } from "react";
import { useWorkspaceObjectsDetailed } from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import OverflowTabBar, { TabDefinition } from "../../../../shared/ui/tabs/OverflowTabBar";

type AccountRoutesProps = {
  className?: string;
};

/** Tabs that are always shown regardless of workspace objects */
const CORE_TABS: TabDefinition[] = [
  { to: "overview", label: "Overview", icon: Sparkles as any },
  { to: "traits", label: "Traits", icon: List as any },
  { to: "users", label: "Users", icon: Users as any },
];

/** Tabs that are always shown at the end */
const TAIL_TABS: TabDefinition[] = [
  { to: "docs", label: "Docs", icon: FileText as any },
];

const EXPLORER_TAB: TabDefinition = {
  to: "event-explorer",
  label: "Event Explorer",
  icon: Search as any,
  children: [
    { to: "events", label: "Product Usage", icon: MousePointerClick as any },
    { to: "trends", label: "Metric Trends", icon: TrendingUp as any },
  ],
};

/**
 * Objects with dedicated routes use custom paths instead of `objects/{slug}`.
 * All other enabled objects use the generic `objects/{slug}` route.
 */
const DEDICATED_ROUTE_BY_SLUG: Record<string, string> = {
  conversations: "emails",
  meetings: "calendar",
};

/** Slugs that already have their own core tabs or should be excluded */
const EXCLUDED_SLUGS = new Set(["accounts", "users", "conversation_messages", "docs", "skills"]);

/** Icon mapping by object slug — keeps icons consistent with settings page */
const ICON_BY_SLUG: Record<string, React.ComponentType<any>> = {
  conversations: MessagesSquare,
  conversation_messages: MessageCircle,
  meetings: Calendar,
  meeting_recordings: Video,
  deals: Handshake,
  tasks: CheckSquare,
  notes: FileText,
  companies: Building2,
};

const AccountRoutes = ({ className = '' }: AccountRoutesProps) => {
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);

  const { data: workspaceObjects } = useWorkspaceObjectsDetailed({
    workspaceId: workspaceId || "",
    enabled: !!workspaceId,
  }) as any;

  const tabs = useMemo(() => {
    const objectTabs: TabDefinition[] = [];

    if (workspaceObjects) {
      const objects = Array.isArray(workspaceObjects) ? workspaceObjects : [];
      objects
        .filter((obj: any) => obj.enabled && !EXCLUDED_SLUGS.has(obj.api_slug))
        .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .forEach((obj: any) => {
          const slug: string = obj.api_slug;
          const route = DEDICATED_ROUTE_BY_SLUG[slug] || `objects/${slug}`;
          const icon = ICON_BY_SLUG[slug];

          objectTabs.push({
            to: route,
            label: obj.display_name || obj.plural_noun || slug,
            ...(icon ? { icon: icon as any } : {}),
          });
        });
    }

    return [...CORE_TABS, ...objectTabs, ...TAIL_TABS, EXPLORER_TAB];
  }, [workspaceObjects]);

  return <OverflowTabBar tabs={tabs} className={className} />;
};

export default AccountRoutes;
